import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { CookieBanner } from "../components/cookie-banner";
import { AnalyticsScripts } from "../components/analytics-scripts";
import { I18nProvider } from "../lib/i18n";
import { AutoTranslate } from "../components/auto-translate";
import { ThemeProvider, themeInitScript } from "../lib/theme";


function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: unknown; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Mine Class | Capacitación para Empresas Mineras" },
      {
        name: "description",
        content:
          "Optimiza la capacitación de la dotación minera con Mine Class. LMS móvil, cursos corporativos y tecnología con IA para la gestión de personas en faena.",
      },
      { property: "og:site_name", content: "Mine Class" },
      { property: "og:title", content: "Mine Class | Capacitación para Empresas Mineras" },
      {
        property: "og:description",
        content: "Optimiza la capacitación de la dotación minera con Mine Class. LMS móvil, cursos corporativos y tecnología con IA para la gestión de personas en faena.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Mine Class | Capacitación para Empresas Mineras" },
      { name: "twitter:description", content: "Optimiza la capacitación de la dotación minera con Mine Class. LMS móvil, cursos corporativos y tecnología con IA para la gestión de personas en faena." },
      { property: "og:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/ovVgHQp1IyZCwd6dfoJok5inlZ43/social-images/social-1784902972696-page_mine_class.webp" },
      { name: "twitter:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/ovVgHQp1IyZCwd6dfoJok5inlZ43/social-images/social-1784902972696-page_mine_class.webp" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@24,400,0,0&display=swap",
      },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Raleway:wght@400;500;600;700;800&display=swap",
      },
    ],
    scripts: [
      { children: themeInitScript },
      {
        children:
          'function initFreshChat(){window.fcWidget.init({token:"ec69ee9c-2043-4812-a037-2e7ab9261845",host:"https://mine-class.freshchat.com",widgetUuid:"8d48c8d5-0ba7-4dcb-89da-da274647f833"})}function initialize(i,t){var e;i.getElementById(t)?initFreshChat():((e=i.createElement("script")).id=t,e.async=!0,e.src="https://mine-class.freshchat.com/js/widget.js",e.onload=initFreshChat,i.head.appendChild(e))}function initiateCall(){initialize(document,"Freshchat-js-sdk")}window.addEventListener?window.addEventListener("load",initiateCall,!1):window.attachEvent("load",initiateCall,!1);',
      },
      {
        children:
          'function loadScript(a){var b=document.getElementsByTagName("head")[0],c=document.createElement("script");c.type="text/javascript",c.src="https://tracker.metricool.com/resources/be.js",c.onreadystatechange=a,c.onload=a,b.appendChild(c)}loadScript(function(){beTracker.t({hash:"2b115c22bd7ce799a887033f3dc84eda"})});',
      },



      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              name: "Mine Class",
              url: "https://mine-class.com/",
              description:
                "Ecosistema Minero 4.0: capacitación, acreditación digital y tecnología propietaria para la industria minera.",
              sameAs: ["https://www.linkedin.com/company/mine-class/"],
            },
            {
              "@type": "WebSite",
              name: "Mine Class",
              url: "https://mine-class.com/",
            },
            {
              "@type": "LocalBusiness",
              "@id": "https://mine-class.com/#sede-chile",
              name: "Mine Class — Chile",
              url: "https://mine-class.com/",
              address: {
                "@type": "PostalAddress",
                streetAddress: "Bucarest 150, oficina 802",
                addressLocality: "Providencia",
                addressRegion: "Región Metropolitana",
                addressCountry: "CL",
              },
            },
            {
              "@type": "LocalBusiness",
              "@id": "https://mine-class.com/#sede-peru",
              name: "Mine Class — Perú",
              url: "https://mine-class.com/",
              address: {
                "@type": "PostalAddress",
                streetAddress: "Av. Don Diego de Día 228",
                addressLocality: "Santiago de Surco",
                addressRegion: "Lima",
                addressCountry: "PE",
              },
            },
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function GeoRedirect() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const host = window.location.hostname;
    if (!host.includes("mine-class.com")) return;

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 2000);

    fetch("https://ipwho.is/", { signal: controller.signal })
      .then((res) => res.json())
      .then((data) => {
        if (data?.success !== false && data?.country_code === "PE") {
          const dest = `https://mine-class.pe${window.location.pathname}${window.location.search}${window.location.hash}`;
          window.location.replace(dest);
        }
      })
      .catch(() => {})
      .finally(() => clearTimeout(timeout));
  }, []);

  return null;
}

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <I18nProvider>
          {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
          <Outlet />
          <AutoTranslate />
          <CookieBanner />
          <AnalyticsScripts />
          <GeoRedirect />
        </I18nProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

